import { FC, useMemo, useState } from "react";
import { inject, observer } from "mobx-react";
import LoginAsUserStore from "./store";
import { useTranslation } from "react-i18next";
import { Button, Table } from "../../../components";
import { BiLogIn, BiUser } from "react-icons/bi";
import { MessageResultState } from "../../../types/general-types";
import { setSessionKey, setUser } from "../../../utils/SsoSession";
import moment from "moment";
import ExplanationModal from "./components/explanation-modal/explanation-modal";
import { SSOExternalUserGridListDto, SSOLoginAsUserGridListDto } from "./types";
import { TabContent, TabPane, Nav, NavItem, NavLink } from "reactstrap";

interface IPage {
  LoginAsUserStore?: LoginAsUserStore;
}

const LoginAsUserPage: FC<IPage> = inject("LoginAsUserStore")(
  observer((props: IPage) => {
    const { LoginAsUserStore: store } = props;
    const { t } = useTranslation();
    const { users, externalusers } = store!;
    const [activeTabIndex, setActiveTabIndex] = useState(0);
    const [showModal, setShowModal] = useState(false);
    const toggleShowModal = () => setShowModal(!showModal);
    const [selectedRow, setSelectedRow] = useState<SSOLoginAsUserGridListDto>();
    const [selectedExternalUser, setSelectedExternalUser] =
      useState<SSOExternalUserGridListDto>();
    const [isExternal, setIsExternal] = useState<boolean>(false);

    const externalUserColumns = useMemo(
      () => [
        {
          Header: t("Actions"),
          id: "actions",
          Cell: ({ row }: any) => {
            row = Object.assign({}, row.original);
            return (
              <Button
                className="btn btn-link text-success"
                icon={<BiLogIn style={{ marginRight: 3 }} />}
                tooltipText={t("Login as The User")}
                onClick={async () => {
                  setSelectedExternalUser(row);
                  setIsExternal(true);
                  setShowModal(true);
                }}
              />
            );
          },
          className: "d-flex justify-content-center text-center",
        },
        {
          Header: t("Name Surname"),
          id: "nameSurname",
          accessor: "nameSurname",
          Cell: ({ row }: any) => {
            return (
              <span
                dangerouslySetInnerHTML={{ __html: row.values.nameSurname }}
              />
            );
          },
          className: "d-none d-md-table-cell",
        },
        {
          Header: t("Email"),
          id: "email",
          accessor: "email",
          className: "d-none d-md-table-cell",
        },
        {
          Header: t("Phone Number"),
          id: "phoneNumber",
          accessor: "phoneNumber",
          className: "d-none d-md-table-cell",
        },
        {
          Header: t("User Name"),
          id: "username",
          accessor: "username",
          Cell: ({ row }: any) => {
            return (
              <span dangerouslySetInnerHTML={{ __html: row.values.username }} />
            );
          },
        },
      ],
      [store, t]
    );

    const columns = useMemo(
      () => [
        {
          Header: t("Actions"),
          id: "actions",
          Cell: ({ row }: any) => {
            row = Object.assign({}, row.original);
            return (
              <Button
                className="btn btn-link text-success"
                icon={<BiLogIn style={{ marginRight: 3 }} />}
                tooltipText={t("Login as The User")}
                onClick={async () => {
                  setSelectedRow(row);
                  setIsExternal(false);
                  setShowModal(true);
                }}
              />
            );
          },
          className: "d-flex justify-content-center text-center",
        },
        {
          Header: t("Personnel Id"),
          id: "personnelId",
          accessor: "personnelId",
          Cell: ({ row }: any) => {
            return (
              <span
                dangerouslySetInnerHTML={{ __html: row.values.personnelId }}
              />
            );
          },
          className: "d-none d-md-table-cell",
        },
        {
          Header: t("Full Name"),
          id: "name",
          accessor: "name",
          Cell: ({ row }: any) => {
            return (
              <span dangerouslySetInnerHTML={{ __html: row.values.name }} />
            );
          },
        },
        {
          Header: t("Email"),
          id: "email",
          accessor: "email",
          className: "d-none d-md-table-cell",
        },
        {
          Header: t("Job"),
          id: "job",
          accessor: "job",
          className: "d-none d-md-table-cell",
        },
        {
          Header: t("Job Started Date"),
          id: "startDate",
          accessor: (row: any) => {
            return moment(row.startDate).format("LL");
          },
          className: "d-none d-md-table-cell",
        },
      ],
      [store, t]
    );

    const page = useMemo(() => {
      return (
        <>
          <div className="card-header pl-2">
            <Nav tabs className="w-auto pl-0">
              <NavItem>
                <NavLink
                  onClick={() => {
                    setActiveTabIndex(0);
                  }}
                  active={activeTabIndex === 0}
                  style={{ cursor: "pointer" }}
                >
                  {t("Securitas Users")}
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  onClick={() => {
                    setActiveTabIndex(1);
                  }}
                  active={activeTabIndex === 1}
                  style={{ cursor: "pointer" }}
                >
                  {t("External Users")}
                </NavLink>
              </NavItem>
            </Nav>
          </div>
          <TabContent activeTab={activeTabIndex}>
            <TabPane tabId={0}>
              <div className="card body">
                <div className="p-3 p-lg-5">
                  <Table
                    columns={columns}
                    smallSize={true}
                    totalCount={users.totalCount}
                    data={users.items}
                    fetchData={store?.all}
                    isLoading={!users.isLoaded}
                  />
                </div>
              </div>
            </TabPane>
            <TabPane tabId={1}>
              <div className="card body">
                <div className="p-3 p-lg-5">
                  <Table
                    columns={externalUserColumns}
                    smallSize={true}
                    totalCount={externalusers.totalCount}
                    data={externalusers.items}
                    fetchData={store?.allExternal}
                    isLoading={!externalusers.isLoaded}
                  />
                </div>
              </div>
            </TabPane>
          </TabContent>
          {showModal && (
            <ExplanationModal
              isShow={showModal}
              toggleModal={toggleShowModal}
              selectedRow={selectedRow}
              selectedExternalUser={selectedExternalUser}
              isExternal={isExternal}
              t={t}
            />
          )}
        </>
      );
    }, [
      columns,
      users.totalCount,
      users.items,
      users.isLoaded,
      store?.all,
      showModal,
      selectedRow,
      externalusers.totalCount,
      externalusers.items,
      externalusers.isLoaded,
      selectedExternalUser,
      externalUserColumns,
      store?.allExternal,
      activeTabIndex,
    ]);

    return <>{page}</>;
  })
);

export default LoginAsUserPage;
